<script lang="ts">
import { Options } from "vue-class-component";
import { Prop } from "@/helpers/Decorators";
import { Form, FormType } from "@/helpers/Form";
import LoaderMixin from "./loaderMixin";
import Pager from "@/helpers/Pager";
import CommentsService, { CommentsListItemModel } from "../services/CommentsService";
import StorageService, { Resource } from "@/modules/core/files/services/StorageService";
import { UserDisplayModel } from "@/modules/core/dictionaries/services/DictionaryService";
import { formatFileSize } from "@/helpers/Utils";
import Substitute from '@/modules/core/substitutions/views/Substitute.vue';
import FileList from "@/components/common/FileList.vue";

const combinedMention = /@\[(\d+)(?:\|([^\]]*))?\]|\@\((\d+)\)\[([^\]]*)\]/g;

export type CommentSegment =
    | { type: "text"; text: string }
    | { type: "mention"; userId: number; label: string };

@Options({
    name: "Comments",
    components: { Substitute, FileList },
})
export default class Comments extends LoaderMixin
{
    @Prop({ default: "" }) public objectId: string;
    @Prop({ default: "", required: true }) public moduleName: string;
    @Prop({ default: false }) public design: boolean;
    @Prop({ default: "" }) public blueprintName: string;
    @Prop({ default: "" }) public blueprintId: string;
    @Prop({ default: 2 }) public maxAttachments: number;
    @Prop({ default: 10 }) public maxAttachmentSizeMb: number;

    public form: FormType<any> = Form.create<any>({
        comment: "",
    });
    public attachments: any[] = [];
    public isDragOver = false;

    public comments: CommentsListItemModel[] = [];
    public filter = Form.create({});
    public pager = new Pager(1, 10, "dateCreatedUtc", "DESC");

    public mentionOpen = false;
    public mentionStart = 0;
    public mentionQuery = "";
    public mentionUsers: UserDisplayModel[] = [];
    public mentionLoading = false;
    public mentionFetchTimer: ReturnType<typeof setTimeout> | null = null;
    public taggedUsersCache = new Map<string, UserDisplayModel[]>();
    public mentionCaretPayload = 0;
    public mentionDropdownTop = 0;
    public mentionDropdownLeft = 0;
    /** Keyboard focus index in mention list; -1 when empty or not yet set. */
    public mentionHighlightIndex = -1;

    public get lastPage(): boolean
    {
        return this.pager.getPageSize() >= this.pager.getTotalRows();
    }

    public async created(): Promise<void>
    {
        this.pager.setTotalRows(0);
        !this.design && (await this.init());
    }

    public mounted(): void
    {
        if (!this.design)
        {
            this.$nextTick(() => this.syncEditorFromPayload());
        }
    }

    public showInfo(id: number): void
    {
        this.$events.$emit("fetchUser", { id });
    }

    public async fetchData(): Promise<void>
    {
        try
        {
            const { totalRows, items } = await CommentsService.getCommentsList(this.objectId, this.moduleName, this.filter.data(), this.pager);

            this.pager.setTotalRows(totalRows);
            this.comments = items.map((item) => item.result);
        }
        catch (ex)
        {
            this.comments = [];
            this.pager.setTotalRows(0);
            this.handleException(ex, {
                400: (ex: any) => this.$alert.warning(ex.message),
            });
        }
        finally
        {
            this.form.continue();
        }
    }

    public fetchNextPart(): void
    {
        this.pager.setPageSize(this.pager.getPageSize() + 10);
        this.fetchData();
    }

    public openAttachmentDialog(): void
    {
        const input = this.$refs.commentAttachmentsInput as HTMLInputElement;

        input?.click();
    }

    public onAttachmentsSelected(event: Event): void
    {
        const input = event.target as HTMLInputElement;
        const files = Array.from(input.files ?? []);

        this.addAttachments(files);
        input.value = "";
    }

    public onDragOver(): void
    {
        this.isDragOver = true;
    }

    public onDragLeave(): void
    {
        this.isDragOver = false;
    }

    public onDrop(event: DragEvent): void
    {
        this.isDragOver = false;

        const files = Array.from(event.dataTransfer?.files ?? []);

        this.addAttachments(files);
    }

    private addAttachments(files: File[]): void
    {
        if (files.length == 0)
            return;


        const maxBytes = this.maxAttachmentSizeMb * 1024 * 1024;
        const nextAttachments = [...this.attachments];

        for (const file of files)
        {
            if (nextAttachments.length >= this.maxAttachments)
            {
                this.$alert.warning(this.$t('[[[Maksymalna liczba załączników została przekroczona.]]]'));
                break;
            }

            if (maxBytes > 0 && file.size > maxBytes)
            {
                this.$alert.warning(
                    this.$t(
                        '[[[Plik "%0" przekracza dopuszczalny rozmiar %1 MB.|||{0}|||{1}]]]',
                        file.name,
                        this.maxAttachmentSizeMb.toString()
                    )
                );
                continue;
            }

            const duplicate = nextAttachments.some((attachment: any) => attachment.name === file.name && attachment.upload?.size === file.size);

            if (duplicate)
                continue;

            nextAttachments.push({
                name: file.name,
                upload: file,
                size: file.size,
            });
        }

        this.attachments = nextAttachments;
    }

    public removeAttachment(index: number): void
    {
        this.attachments.splice(index, 1);
    }

    public fileSizeBeautifier(sizeInBytes: number): string
    {
        return formatFileSize(sizeInBytes / 1024);
    }

    public toFileListResources(attachments: { publicId: string; fileName: string }[]): Resource[]
    {
        return (attachments ?? []).map((attachment) => ({
            id: 0,
            publicId: attachment.publicId,
            resourceType: "file",
            name: attachment.fileName,
            url: "",
        }));
    }

    public commentSegments(text: string | undefined | null): CommentSegment[]
    {
        const comment = text ?? "";
        const segments: CommentSegment[] = [];
        let last = 0;
        let m: RegExpExecArray | null;

        combinedMention.lastIndex = 0;

        while ((m = combinedMention.exec(comment)) != null)
        {
            if (m.index > last)
            {
                segments.push({ type: "text", text: comment.slice(last, m.index) });
            }

            const id = m[1] ?? m[3]!;
            const rawLabel = (m[2] ?? m[4] ?? "").trim();
            const label = rawLabel !== "" ? rawLabel : `#${id}`;

            segments.push({ type: "mention", userId: Number(id), label });
            last = m.index + m[0].length;
        }

        if (last < comment.length)
        {
            segments.push({ type: "text", text: comment.slice(last) });
        }

        if (segments.length === 0)
        {
            segments.push({ type: "text", text: comment });
        }

        return segments;
    }

    public onCommentInput(event: Event): void
    {
        const ta = event.target as HTMLTextAreaElement;
        const caret = ta.selectionStart ?? this.form.comment.length;

        this.syncMentionState(this.form.comment, caret);
    }

    public onEditorInput(): void
    {
        if (this.design)
            return;

        const el = this.getCommentEditorEl();

        if (!el)
            return;

        this.form.comment = this.editorToPayload(el);
        const caret = this.getCaretPayloadOffset(el);

        this.syncMentionState(this.form.comment, caret);

        if (this.mentionOpen)
        {
            this.mentionCaretPayload = caret;
            this.$nextTick(() => this.updateMentionDropdownPosition());
        }
    }

    public onEditorKeydown(e: KeyboardEvent): void
    {
        this.form.$errors.clear("comment");

        if (e.key === "Escape" && this.mentionOpen)
        {
            this.mentionOpen = false;
            this.mentionHighlightIndex = -1;
            e.preventDefault();

            return;
        }

        if (this.mentionOpen && (e.key === "ArrowDown" || e.key === "ArrowUp"))
        {
            e.preventDefault();

            if (!this.mentionLoading && this.mentionUsers.length > 0)
            {
                if (e.key === "ArrowDown")
                {
                    if (this.mentionHighlightIndex < 0)
                        this.mentionHighlightIndex = 0;
                    else
                        this.mentionHighlightIndex = Math.min(this.mentionHighlightIndex + 1, this.mentionUsers.length - 1);
                }
                else if (this.mentionHighlightIndex > 0)
                {
                    this.mentionHighlightIndex -= 1;
                }
                else
                {
                    this.mentionHighlightIndex = 0;
                }

                this.$nextTick(() => this.scrollMentionHighlightIntoView());
            }

            return;
        }

        if (e.key === "Enter")
        {
            if (
                this.mentionOpen
                && !e.shiftKey
                && !this.mentionLoading
                && this.mentionUsers.length > 0
                && this.mentionHighlightIndex >= 0
                && this.mentionHighlightIndex < this.mentionUsers.length
            )
            {
                const pick = this.mentionUsers[this.mentionHighlightIndex];

                if (pick)
                {
                    e.preventDefault();
                    this.selectMention(pick);

                    return;
                }
            }

            e.preventDefault();
            document.execCommand("insertText", false, "\n");
            this.$nextTick(() => this.onEditorInput());
        }
    }

    public onFormAreaKeydown(e: KeyboardEvent): void
    {
        const t = e.target as HTMLElement & { name?: string };
        const name = t?.name;

        if (name)
            this.form.$errors.clear(name);
    }

    public onEditorPaste(e: ClipboardEvent): void
    {
        e.preventDefault();
        const text = e.clipboardData?.getData("text/plain") ?? "";
        const sel = window.getSelection();

        if (!sel?.rangeCount)
            return;

        const r = sel.getRangeAt(0);

        r.deleteContents();
        const n = document.createTextNode(text);

        r.insertNode(n);
        r.setStartAfter(n);
        r.collapse(true);
        sel.removeAllRanges();
        sel.addRange(r);
        this.onEditorInput();
    }

    private syncMentionState(text: string, caret: number): void
    {
        const beforeCaret = text.slice(0, caret);
        const at = beforeCaret.lastIndexOf("@");

        if (at < 0)
        {
            this.mentionOpen = false;
            this.mentionHighlightIndex = -1;

            return;
        }

        const afterAt = beforeCaret.slice(at + 1);

        if (afterAt.includes(" ") || afterAt.includes("\n") || afterAt.includes("\t"))
        {
            this.mentionOpen = false;
            this.mentionHighlightIndex = -1;

            return;
        }

        this.mentionOpen = true;
        this.mentionStart = at;
        this.mentionQuery = afterAt;
        this.mentionCaretPayload = caret;
        this.mentionHighlightIndex = -1;
        this.scheduleMentionFetch();
        this.$nextTick(() => this.updateMentionDropdownPosition());
    }

    private scheduleMentionFetch(): void
    {
        if (this.mentionFetchTimer != null)
        {
            clearTimeout(this.mentionFetchTimer);
        }

        this.mentionFetchTimer = setTimeout(() => this.loadMentionUsers(), 250);
    }

    private async loadMentionUsers(): Promise<void>
    {
        if (!this.mentionOpen || this.design || !this.objectId || !this.moduleName)
        {
            return;
        }

        const q = this.mentionQuery;
        const cacheKey = `${this.moduleName}:${this.objectId}:${q}`;

        if (this.taggedUsersCache.has(cacheKey))
        {
            this.mentionUsers = this.taggedUsersCache.get(cacheKey) ?? [];
            this.clampMentionHighlight();

            return;
        }

        this.mentionLoading = true;

        try
        {
            const { items } = await CommentsService.getTaggedUsers(this.objectId, this.moduleName, q);
            const users = items.map((row) => row.result);

            this.mentionUsers = users;
            this.taggedUsersCache.set(cacheKey, users);
        }
        catch
        {
            this.mentionUsers = [];
        }
        finally
        {
            this.mentionLoading = false;
            this.clampMentionHighlight();
        }
    }

    private clampMentionHighlight(): void
    {
        if (!this.mentionUsers.length)
        {
            this.mentionHighlightIndex = -1;

            return;
        }

        if (this.mentionHighlightIndex < 0)
            this.mentionHighlightIndex = 0;

        if (this.mentionHighlightIndex >= this.mentionUsers.length)
            this.mentionHighlightIndex = this.mentionUsers.length - 1;
    }

    private scrollMentionHighlightIntoView(): void
    {
        const root = this.$refs.mentionDropdown as HTMLElement | undefined;
        const el = root?.querySelector?.(`[data-mention-idx="${this.mentionHighlightIndex}"]`) as HTMLElement | undefined;

        el?.scrollIntoView({ block: "nearest" });
    }

    public selectMention(user: UserDisplayModel, event?: Event): void
    {
        event?.preventDefault();

        if (this.design)
            return;

        const el = this.getCommentEditorEl();

        if (!el)
            return;

        const text = this.form.comment;
        const caret = this.mentionCaretPayload;
        const before = text.slice(0, this.mentionStart);
        const after = text.slice(caret);
        const id = String((user as any).key ?? user.key);
        const name = (user.value ?? user.name ?? "").trim();
        const token = name !== "" ? `@[${id}|${name}]` : `@[${id}]`;

        this.form.comment = `${before}${token}${after}`;
        this.mentionOpen = false;
        this.mentionUsers = [];
        this.mentionHighlightIndex = -1;

        this.$nextTick(() =>
        {
            this.applyPayloadToDom(el, this.form.comment);
            el.focus();
            const pos = before.length + token.length;

            this.setCaretPayloadOffset(el, pos);
        });
    }

    private getCommentEditorEl(): HTMLElement | null
    {
        return (this.$refs.commentEditor as HTMLElement | undefined) ?? null;
    }

    private payloadToFragment(payload: string): DocumentFragment
    {
        const frag = document.createDocumentFragment();
        const re = /@\[(\d+)(?:\|([^\]]*))?\]|\@\((\d+)\)\[([^\]]*)\]/g;
        let last = 0;
        let m: RegExpExecArray | null;

        while ((m = re.exec(payload)) != null)
        {
            if (m.index > last)
            {
                frag.appendChild(document.createTextNode(payload.slice(last, m.index)));
            }

            const id = m[1] ?? m[3]!;
            const rawName = (m[2] ?? m[4] ?? "").trim();
            const label = rawName !== "" ? rawName : `#${id}`;
            const token = rawName !== "" ? `@[${id}|${rawName}]` : `@[${id}]`;
            const span = document.createElement("span");

            span.className = "comment-mention comment-mention-chip";
            span.contentEditable = "false";
            span.setAttribute("data-token", token);
            span.textContent = `@${label}`;
            frag.appendChild(span);
            last = m.index + m[0].length;
        }

        if (last < payload.length)
        {
            frag.appendChild(document.createTextNode(payload.slice(last)));
        }

        return frag;
    }

    private applyPayloadToDom(editor: HTMLElement, payload: string): void
    {
        editor.innerHTML = "";
        editor.appendChild(this.payloadToFragment(payload));
    }

    private syncEditorFromPayload(): void
    {
        const el = this.getCommentEditorEl();

        if (!el)
            return;

        this.applyPayloadToDom(el, this.form.comment ?? "");
    }

    private editorToPayload(editor: HTMLElement): string
    {
        let out = "";

        const walk = (node: Node): void =>
        {
            if (node.nodeType === Node.TEXT_NODE)
            {
                out += (node as Text).data;

                return;
            }

            if (node.nodeType !== Node.ELEMENT_NODE)
            {
                return;
            }

            const el = node as HTMLElement;

            if (el.classList.contains("comment-mention-chip"))
            {
                out += el.getAttribute("data-token") ?? "";

                return;
            }

            if (el.tagName === "BR")
            {
                out += "\n";

                return;
            }

            for (const c of Array.from(el.childNodes))
            {
                walk(c);
            }

            if (el.tagName === "DIV" && el !== editor)
            {
                out += "\n";
            }
        };

        for (const c of Array.from(editor.childNodes))
        {
            walk(c);
        }

        return out;
    }

    private getCaretPayloadOffset(editor: HTMLElement): number
    {
        const sel = window.getSelection();

        if (!sel || sel.rangeCount === 0)
            return 0;

        const r = sel.getRangeAt(0).cloneRange();
        const pre = document.createRange();

        pre.selectNodeContents(editor);
        pre.setEnd(r.endContainer, r.endOffset);
        const frag = pre.cloneContents();
        const holder = document.createElement("div");

        holder.appendChild(frag);

        return this.editorToPayload(holder).length;
    }

    private setCaretPayloadOffset(editor: HTMLElement, target: number): void
    {
        let rem = target;

        const place = (nodes: ArrayLike<ChildNode>): Range | null =>
        {
            for (let i = 0; i < nodes.length; i++)
            {
                const node = nodes[i]!;

                if (node.nodeType === Node.TEXT_NODE)
                {
                    const tn = node as Text;
                    const len = tn.length;

                    if (rem <= len)
                    {
                        const rr = document.createRange();

                        rr.setStart(tn, Math.max(0, rem));
                        rr.collapse(true);

                        return rr;
                    }

                    rem -= len;
                    continue;
                }

                if (node.nodeType === Node.ELEMENT_NODE)
                {
                    const el = node as HTMLElement;

                    if (el.classList.contains("comment-mention-chip"))
                    {
                        const token = el.getAttribute("data-token") ?? "";
                        const tl = token.length;

                        if (rem <= 0)
                        {
                            const rr = document.createRange();

                            rr.setStartBefore(el);
                            rr.collapse(true);

                            return rr;
                        }

                        if (rem <= tl)
                        {
                            const rr = document.createRange();

                            rr.setStartAfter(el);
                            rr.collapse(true);

                            return rr;
                        }

                        rem -= tl;
                        continue;
                    }

                    if (el.tagName === "BR")
                    {
                        if (rem <= 0)
                        {
                            const rr = document.createRange();

                            rr.setStartBefore(el);
                            rr.collapse(true);

                            return rr;
                        }

                        if (rem <= 1)
                        {
                            const rr = document.createRange();

                            rr.setStartAfter(el);
                            rr.collapse(true);

                            return rr;
                        }

                        rem -= 1;
                        continue;
                    }

                    const inner = place(el.childNodes);

                    if (inner)
                        return inner;
                }
            }

            return null;
        };

        let r = place(editor.childNodes);

        if (!r)
        {
            r = document.createRange();
            r.selectNodeContents(editor);
            r.collapse(false);
        }

        const sel = window.getSelection();

        sel?.removeAllRanges();
        sel?.addRange(r);
    }

    private updateMentionDropdownPosition(): void
    {
        if (!this.mentionOpen || this.design)
            return;

        const sel = window.getSelection();

        if (!sel || sel.rangeCount === 0)
            return;

        const r = sel.getRangeAt(0).cloneRange();

        r.collapse(true);
        const rects = r.getClientRects();
        const rect = rects.length > 0 ? rects[0]! : r.getBoundingClientRect();

        if (rect.width === 0 && rect.height === 0 && rects.length === 0)
        {
            const ed = this.getCommentEditorEl();

            if (ed)
            {
                const cr = ed.getBoundingClientRect();

                this.mentionDropdownTop = cr.top + 24;
                this.mentionDropdownLeft = cr.left + 8;
            }

            return;
        }

        this.mentionDropdownTop = rect.bottom + 4;
        this.mentionDropdownLeft = rect.left;
    }

    public async saveComment(): Promise<void>
    {
        this.form.wait();

        const uploadedAttachmentIds: string[] = [];

        try
        {
            if (this.attachments.length > this.maxAttachments)
            {
                this.$alert.warning(this.$t('[[[Maksymalna liczba załączników została przekroczona.]]]'));

                return;
            }

            const maxBytes = this.maxAttachmentSizeMb * 1024 * 1024;

            if (maxBytes > 0 && this.attachments.some((p: any) => p.upload && p.upload.size > maxBytes))
            {
                this.$alert.warning(this.$t('[[[Co najmniej jeden załącznik przekracza dopuszczalny rozmiar.]]]'));

                return;
            }

            for (const attachment of this.attachments)
            {
                if (attachment?.publicId)
                {
                    uploadedAttachmentIds.push(attachment.publicId);
                    continue;
                }

                const file = await StorageService.upload(attachment.upload, 'documents');

                uploadedAttachmentIds.push(file.publicId);
            }

            await CommentsService.saveComment(this.objectId, this.moduleName, this.form.comment, uploadedAttachmentIds, this.blueprintId);
            await this.fetchData();
            this.$alert.success(this.$t("[[[Komentarz został dodany.]]]"));
            this.form.clear();
            this.attachments = [];
            this.$nextTick(() => this.syncEditorFromPayload());
        }
        catch (ex)
        {
            if (uploadedAttachmentIds.length > 0)
            {
                await CommentsService.cleanupTemporaryAttachments(uploadedAttachmentIds);
            }

            this.handleException(ex, {
                400: (ex: any) => this.$alert.warning(ex.message),
                422: (ex: any) => [
                    this.form.$errors.record(ex.data.errors),
                    this.$scrollTo(".invalid-feedback"),
                    this.$alert.warning(this.$t("[[[Nie wszystkie pola zostały wypełnione prawidłowo.]]]")),
                ],
            });
        }
        finally
        {
            this.form.continue();
        }
    }
}
</script>

<template>
    <div class="row">
        <div
            class="col-md-12"
            :class="{ 'comment-dropzone': isDragOver }"
            @keydown="onFormAreaKeydown"
            @dragover.prevent="onDragOver"
            @dragleave.prevent="onDragLeave"
            @drop.prevent="onDrop"
        >
            <IdeoFormGroup :invalid-feedback="form.$errors.first('comment')" :state="form.$errors.state('comment')" nospace>
                <div class="position-relative">
                    <IdeoFormTextarea
                        v-if="design"
                        ref="commentTa"
                        v-model="form.comment"
                        :placeholder="$t('[[[Wpisz komentarz...]]]')"
                        :rows="5"
                        @native-input="onCommentInput"
                    />
                    <template v-else>
                        <div
                            ref="commentEditor"
                            class="comment-editor form-control"
                            contenteditable="true"
                            role="textbox"
                            aria-multiline="true"
                            spellcheck="true"
                            :data-placeholder="$t('[[[Wpisz komentarz...]]]')"
                            :class="{ 'is-invalid': form.$errors.state('comment') === false }"
                            @input="onEditorInput"
                            @keydown="onEditorKeydown"
                            @paste="onEditorPaste"
                        />
                        <input type="hidden" name="comment" :value="form.comment" />
                    </template>
                    <Teleport v-if="mentionOpen && !design" to="body">
                        <div
                            ref="mentionDropdown"
                            class="mention-dropdown list-group shadow-sm"
                            role="listbox"
                            :aria-activedescendant="
                                !mentionLoading && mentionHighlightIndex >= 0 && mentionUsers.length
                                    ? `mention-opt-${mentionHighlightIndex}`
                                    : undefined
                            "
                            :style="{ top: mentionDropdownTop + 'px', left: mentionDropdownLeft + 'px' }"
                        >
                            <div v-if="mentionLoading" class="list-group-item text-muted small">
                                {{ $t("[[[Ładowanie…]]]") }}
                            </div>
                            <button
                                v-for="(u, idx) in mentionUsers"
                                :id="`mention-opt-${idx}`"
                                :key="`${u.key}-${idx}`"
                                type="button"
                                class="list-group-item list-group-item-action mention-item"
                                role="option"
                                :data-mention-idx="idx"
                                :class="{ active: idx === mentionHighlightIndex }"
                                :aria-selected="idx === mentionHighlightIndex"
                                @mouseenter="mentionHighlightIndex = idx"
                                @mousedown.prevent="selectMention(u, $event)"
                            >
                                <span class="fw-semibold">{{ u.value }}</span>
                                <span v-if="u.unit" class="text-muted small ms-1">{{ u.unit }}</span>
                            </button>
                            <div
                                v-if="!mentionLoading && mentionUsers.length === 0"
                                class="list-group-item text-muted small"
                            >
                                {{ $t("[[[Brak użytkowników]]]") }}
                            </div>
                        </div>
                    </Teleport>
                </div>
            </IdeoFormGroup>
            <div v-if="attachments.length" class="attachment-bar mt-2">
                <div v-for="(attachment, index) in attachments" :key="`${attachment.name}-${index}`" class="attachment-item">
                    <i class="far fa-file me-2"></i>
                    <span class="attachment-name" :title="attachment.name">{{ attachment.name }}</span>
                    <span class="text-muted ms-1">({{ fileSizeBeautifier(attachment.size ?? 0) }})</span>
                    <button type="button" class="attachment-remove" @click="removeAttachment(index)">
                        <i class="far fa-times"></i>
                    </button>
                </div>
            </div>
            <div class="d-flex align-items-center gap-2 mt-2">
                <input
                    ref="commentAttachmentsInput"
                    type="file"
                    multiple
                    class="d-none"
                    @change="onAttachmentsSelected"
                />
                <IdeoButton class="rounded" variant="success" size="sm" @click="saveComment">
                    <i class="fa fa-plus me-1"></i>
                    {{ $t("[[[Dodaj komentarz]]]") }}
                </IdeoButton>
                <span
                    v-if="maxAttachments > 0"
                    v-tooltip="attachments.length >= maxAttachments ? $t('[[[Osiągnięto maksymalną liczbę załączników]]]') : ''"
                    class="d-inline-block"
                    :title="attachments.length >= maxAttachments ? $t('[[[Osiągnięto maksymalną liczbę załączników]]]') : ''"
                >
                    <IdeoButton
                        variant="outline-secondary"
                        size="sm"
                        class="rounded-circle"
                        :disabled="attachments.length >= maxAttachments"
                        @click="openAttachmentDialog"
                    >
                        <i class="far fa-paperclip"></i>
                    </IdeoButton>
                </span>
            </div>
        </div>
        <div v-if="comments && comments.length" class="col-md-12 mt-3">
            <ul class="list-group">
                <li v-for="(item, index) in comments" :key="index" class="list-group-item list-group-item-action">
                    <div class="d-flex align-items-start mb-1">
                        <div class="d-flex align-items-center flex-shrink-0">
                            <UserAvatar
                                :username="item.author.user.value"
                                :src="$filters.baseurl(`storage/files/${item.author.user.pictureUrl}`)"
                                class="cursor-pointer"
                                @click="showInfo(item.author.user.key)"
                            />
                            <Substitute
                                v-if="item.author.impersonator?.key"
                                :user="item.author.impersonator?.value"
                            />
                        </div>
                        <div class="ps-2">
                            <p class="mb-0 text-primary fw-bold cursor-pointer" @click="showInfo(item.author.user.key)">
                                {{ item.author.user.value }}
                            </p>
                            <p class="mb-0 text-muted small">{{ $filters.datetime(item.author.dateUtc) }}</p>
                            <p class="mb-0 comment-text">
                                <template v-for="(seg, si) in commentSegments(item.comment)">
                                    <span v-if="seg.type === 'text'" :key="`t-${si}`">{{ seg.text }}</span>
                                    <span
                                        v-else
                                        :key="`m-${si}-${seg.userId}`"
                                        class="comment-mention"
                                        role="button"
                                        tabindex="0"
                                        @click="showInfo(seg.userId)"
                                        @keydown.enter.prevent="showInfo(seg.userId)"
                                    >@{{ seg.label }}</span>
                                </template>
                            </p>
                            <div v-if="item.attachments && item.attachments.length" class="mt-2">
                                <FileList :files="toFileListResources(item.attachments)" :is-document="true" />
                            </div>
                        </div>
                    </div>
                </li>
            </ul>
            <div class="d-flex justify-content-center col-12">
                <IdeoButton :disabled="lastPage" variant="primary" size="sm" class="rounded mt-3" @click="fetchNextPart">
                    <i class="fa fa-plus me-1"></i>
                    {{ $t("[[[Pokaż więcej komentarzy]]]") }}
                </IdeoButton>
            </div>
        </div>
        <Portal v-if="blueprintName" :to="`tab-counter-${blueprintName}`">
            {{ pager.getTotalRows() }}
        </Portal>
    </div>
</template>

<style lang="scss" scoped>
.attachment-bar {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.attachment-item {
    display: flex;
    align-items: center;
    border: 1px solid var(--bs-border-color);
    border-radius: 4px;
    padding: 4px 8px;
    font-size: 12px;
    background: var(--bs-body-bg);
    max-width: 100%;
}

.attachment-name {
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.attachment-remove {
    border: none;
    background: transparent;
    color: var(--bs-danger);
    margin-left: 8px;
    padding: 0;
    line-height: 1;
}

.comment-dropzone {
    border: 1px dashed var(--bs-primary);
    border-radius: 6px;
    padding: 8px;
    background: color-mix(in srgb, var(--bs-primary) 8%, transparent);
}

.comment-editor {
    min-height: 120px;
    white-space: pre-wrap;
    word-break: break-word;

    &:empty::before {
        content: attr(data-placeholder);
        color: var(--bs-secondary-color);
        pointer-events: none;
    }

    &:focus {
        outline: none;
    }
}

/* Comment list (template nodes get scoped attribute) */
.comment-mention {
    color: var(--bs-primary);
    font-weight: 600;
    border-radius: 4px;
    padding: 0 3px;
    background: color-mix(in srgb, var(--bs-primary) 14%, transparent);
}

/* Chips are created in JS — no scoped data attribute; pierce from editor */
.comment-editor :deep(.comment-mention-chip) {
    display: inline;
    color: var(--bs-primary);
    font-weight: 600;
    border-radius: 4px;
    padding: 0 3px;
    margin: 0 1px;
    background: color-mix(in srgb, var(--bs-primary) 14%, transparent);
    user-select: none;
    vertical-align: baseline;
    box-decoration-break: clone;
    -webkit-box-decoration-break: clone;
    cursor: default;
    text-decoration: none;
}

.mention-dropdown {
    position: fixed;
    z-index: 1055;
    min-width: 220px;
    max-width: min(420px, 90vw);
    max-height: 220px;
    overflow-y: auto;
    border-radius: 6px;
}

.mention-item {
    cursor: pointer;
    text-align: left;
}

.comment-text .comment-mention {
    cursor: pointer;
}

.comment-text .comment-mention:hover {
    text-decoration: underline;
}

.mention-item.active {
    z-index: 1;
}
</style>
